'use client'

import { useState, forwardRef } from 'react'
import { Info, Shirt, X } from 'lucide-react'
import { cn } from '../utils/styles'

export interface SizeSelectorProps {
  value?: string
  onChange?: (size: string) => void
  error?: string
  required?: boolean
  disabled?: boolean
  sizes?: string[]
  sizeChart?: SizeChart
  showSizeGuide?: boolean
  showCard?: boolean
  className?: string
  label?: string
  variant?: 'default' | 'compact'
  orientation?: 'horizontal' | 'vertical'
  allowCustom?: boolean
  customSizePlaceholder?: string
}

export interface SizeChart {
  [size: string]: {
    eu?: string
    uk?: string
    us?: string
    bust?: string
    waist?: string
    hips?: string
    chest?: string
  }
}

export const DEFAULT_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'] as const

export const DEFAULT_SIZE_CHART: SizeChart = {
  XS: {
    eu: '34-36',
    uk: '6-8',
    us: '2-4',
    bust: '80-84',
    waist: '60-64',
    hips: '86-90',
  },
  S: {
    eu: '36-38',
    uk: '8-10',
    us: '4-6',
    bust: '84-88',
    waist: '64-68',
    hips: '90-94',
  },
  M: {
    eu: '38-40',
    uk: '10-12',
    us: '6-8',
    bust: '88-92',
    waist: '68-72',
    hips: '94-98',
  },
  L: {
    eu: '40-42',
    uk: '12-14',
    us: '8-10',
    bust: '92-96',
    waist: '72-76',
    hips: '98-102',
  },
  XL: {
    eu: '42-44',
    uk: '14-16',
    us: '10-12',
    bust: '96-100',
    waist: '76-80',
    hips: '102-106',
  },
  XXL: {
    eu: '44-46',
    uk: '16-18',
    us: '12-14',
    bust: '100-104',
    waist: '80-84',
    hips: '106-110',
  },
  XXXL: {
    eu: '46-48',
    uk: '18-20',
    us: '14-16',
    bust: '104-108',
    waist: '84-88',
    hips: '110-114',
  },
}

export const SizeSelector = forwardRef<HTMLDivElement, SizeSelectorProps>(({
  value,
  onChange,
  error,
  required = false,
  disabled = false,
  sizes = DEFAULT_SIZES,
  sizeChart = DEFAULT_SIZE_CHART,
  showSizeGuide = true,
  showCard = true,
  className,
  label = "Clothing Size",
  variant = 'default',
  orientation = 'horizontal',
  allowCustom = false,
  customSizePlaceholder = "Enter custom size",
  ...props
}, ref) => {
  const [selectedSize, setSelectedSize] = useState(value || '')
  const [showGuide, setShowGuide] = useState(false)
  const [showCustomInput, setShowCustomInput] = useState(false)
  const [customSize, setCustomSize] = useState('')

  const handleSizeSelect = (size: string) => {
    setSelectedSize(size)
    setShowCustomInput(false)
    setCustomSize('')
    onChange?.(size)
  }

  const handleCustomSize = () => {
    if (customSize.trim()) {
      setSelectedSize(customSize.trim())
      onChange?.(customSize.trim())
      setShowCustomInput(false)
      setCustomSize('')
    }
  }

  const handleCustomSizeCancel = () => {
    setShowCustomInput(false)
    setCustomSize('')
  }

  const renderSizeGuideDialog = () => {
    if (!showGuide) return null

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg max-w-3xl max-h-[80vh] w-full mx-4 overflow-hidden">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">International Size Guide</h2>
                <p className="text-gray-600 dark:text-gray-400">Find your perfect fit with our comprehensive size chart</p>
              </div>
              <button
                onClick={() => setShowGuide(false)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="p-6 overflow-y-auto">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="py-2 text-left">Size</th>
                    {Object.values(sizeChart)[0]?.eu && <th className="py-2 text-left">EU</th>}
                    {Object.values(sizeChart)[0]?.uk && <th className="py-2 text-left">UK</th>}
                    {Object.values(sizeChart)[0]?.us && <th className="py-2 text-left">US</th>}
                    {Object.values(sizeChart)[0]?.bust && <th className="py-2 text-left">Bust (cm)</th>}
                    {Object.values(sizeChart)[0]?.waist && <th className="py-2 text-left">Waist (cm)</th>}
                    {Object.values(sizeChart)[0]?.hips && <th className="py-2 text-left">Hips (cm)</th>}
                    {Object.values(sizeChart)[0]?.chest && <th className="py-2 text-left">Chest (cm)</th>}
                  </tr>
                </thead>
                <tbody>
                  {sizes.map(size => {
                    const measurements = sizeChart[size]
                    if (!measurements) return null

                    return (
                      <tr key={size} className="border-b">
                        <td className="py-2 font-medium">{size}</td>
                        {measurements.eu && <td className="py-2">{measurements.eu}</td>}
                        {measurements.uk && <td className="py-2">{measurements.uk}</td>}
                        {measurements.us && <td className="py-2">{measurements.us}</td>}
                        {measurements.bust && <td className="py-2">{measurements.bust}</td>}
                        {measurements.waist && <td className="py-2">{measurements.waist}</td>}
                        {measurements.hips && <td className="py-2">{measurements.hips}</td>}
                        {measurements.chest && <td className="py-2">{measurements.chest}</td>}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-4 mt-4">
              <h4 className="mb-2 font-medium">How to Measure</h4>
              <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                <li><strong>Bust:</strong> Measure around the fullest part of your chest</li>
                <li><strong>Waist:</strong> Measure around your natural waistline</li>
                <li><strong>Hips:</strong> Measure around the fullest part of your hips</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderSizeButton = (size: string) => (
    <button
      key={size}
      type="button"
      onClick={() => handleSizeSelect(size)}
      disabled={disabled}
      className={cn(
        'h-12 rounded-md border-2 font-medium transition-all',
        'hover:border-primary hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-50',
        selectedSize === size
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-input bg-background',
        variant === 'compact' && 'h-10 text-sm'
      )}
    >
      {size}
    </button>
  )

  const renderCustomInput = () => {
    if (!showCustomInput) return null

    return (
      <div className="flex items-center gap-2 mt-2">
        <input
          type="text"
          value={customSize}
          onChange={(e) => setCustomSize(e.target.value)}
          placeholder={customSizePlaceholder}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleCustomSize()
            if (e.key === 'Escape') handleCustomSizeCancel()
          }}
        />
        <button
          type="button"
          onClick={handleCustomSize}
          className="px-3 py-2 bg-primary text-primary-foreground rounded-md text-sm"
        >
          Add
        </button>
        <button
          type="button"
          onClick={handleCustomSizeCancel}
          className="px-3 py-2 border rounded-md text-sm"
        >
          Cancel
        </button>
      </div>
    )
  }

  const content = (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shirt className="h-5 w-5" />
          <h3 className={cn(
            "font-semibold",
            variant === 'compact' ? 'text-base' : 'text-lg'
          )}>
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </h3>
        </div>
        {showSizeGuide && (
          <button
            type="button"
            onClick={() => setShowGuide(true)}
            className="flex items-center gap-1 px-3 py-1 text-sm border rounded-md hover:bg-gray-50"
          >
            <Info className="h-4 w-4" />
            Size Guide
          </button>
        )}
      </div>

      <div className="space-y-4">
        <div className={cn(
          "grid gap-2",
          orientation === 'horizontal'
            ? variant === 'compact' ? "grid-cols-7" : "grid-cols-4 sm:grid-cols-7"
            : "grid-cols-2 sm:grid-cols-3"
        )}>
          {sizes.map(renderSizeButton)}
        </div>

        {allowCustom && (
          <div>
            {!showCustomInput ? (
              <button
                type="button"
                onClick={() => setShowCustomInput(true)}
                className="text-sm text-primary hover:underline"
                disabled={disabled}
              >
                Add custom size
              </button>
            ) : null}
            {renderCustomInput()}
          </div>
        )}

        {error && <p className="text-sm text-red-500">{error}</p>}
      </div>

      {selectedSize && sizeChart[selectedSize] && (
        <div className="rounded-lg bg-gray-50 dark:bg-gray-900 p-4">
          <p className="mb-2 text-sm font-medium">
            Size {selectedSize} Measurements:
          </p>
          <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 dark:text-gray-400">
            {sizeChart[selectedSize].eu && (
              <div>EU: {sizeChart[selectedSize].eu}</div>
            )}
            {sizeChart[selectedSize].uk && (
              <div>UK: {sizeChart[selectedSize].uk}</div>
            )}
            {sizeChart[selectedSize].us && (
              <div>US: {sizeChart[selectedSize].us}</div>
            )}
            {sizeChart[selectedSize].bust && (
              <div>Bust: {sizeChart[selectedSize].bust} cm</div>
            )}
            {sizeChart[selectedSize].waist && (
              <div>Waist: {sizeChart[selectedSize].waist} cm</div>
            )}
            {sizeChart[selectedSize].hips && (
              <div>Hips: {sizeChart[selectedSize].hips} cm</div>
            )}
            {sizeChart[selectedSize].chest && (
              <div>Chest: {sizeChart[selectedSize].chest} cm</div>
            )}
          </div>
        </div>
      )}
    </div>
  )

  return (
    <>
      {showCard ? (
        <div
          ref={ref}
          className={cn("rounded-lg border bg-card text-card-foreground shadow-sm", className)}
          {...props}
        >
          <div className="p-6">
            {content}
          </div>
        </div>
      ) : (
        <div
          ref={ref}
          className={cn("space-y-4", className)}
          {...props}
        >
          {content}
        </div>
      )}

      {renderSizeGuideDialog()}
    </>
  )
})

SizeSelector.displayName = "SizeSelector"